/**
 * Welke links de app zelf opent en welke in de in-app browser gaan (MOB-2).
 *
 * Pure functies, zonder Capacitor, zodat de regels met een unittest vastliggen. `native-shell.ts`
 * hangt ze aan de app.
 */

/**
 * Hoort deze URL in de app zelf (de router), of in de in-app browser?
 *
 * In de app: de schermen van de eigen host - de app, het beheer en Master. Wie de rechten heeft,
 * wisselt in de app naar het beheer of naar Master (MST-10), en dat moet in hetzelfde venster: de
 * in-app browser heeft eigen cookies, dus daar is niemand aangemeld.
 *
 * Een bestand in de objectopslag gaat wel naar buiten. Een bijlage of een document staat op dezelfde
 * host (`/up2d8-staging/...`, een ondertekende URL), maar een WebView kan een pdf niet tonen; de
 * in-app browser wel, of hij geeft hem aan een pdf-viewer. Een andere host gaat ook naar buiten.
 */
export function opensInApp(url: string, currentOrigin: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url, currentOrigin);
  } catch {
    return false;
  }
  if (parsed.origin !== currentOrigin) return false;
  if (parsed.pathname === '/') return true;
  return IN_APP_ZONES.some(
    (zone) => parsed.pathname === zone || parsed.pathname.startsWith(`${zone}/`),
  );
}

/** De zones van de web-app die in het venster van de app blijven. `/platform` stuurt door naar Master. */
const IN_APP_ZONES = ['/app', '/admin', '/master', '/platform'];

/** Het pad binnen de app voor een deeplink, of `null` als de link niet naar de app wijst. */
export function appPathFromLink(url: string, appOrigin: string): string | null {
  if (!opensInApp(url, appOrigin)) return null;
  const parsed = new URL(url, appOrigin);
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/**
 * Is deze app-versie ouder dan de oudste die nog werkt?
 *
 * Versies als `1.4.2`; ontbrekende delen tellen als 0. Iets wat geen versie is, telt als "niet
 * ouder": liever geen melding dan een melding die iemand niet kan oplossen.
 */
export function isOlderThan(version: string, minimum: string): boolean {
  const parse = (value: string) => value.split('.').map((part) => Number.parseInt(part, 10));
  const current = parse(version);
  const required = parse(minimum);
  if ([...current, ...required].some((part) => Number.isNaN(part))) return false;

  for (let index = 0; index < Math.max(current.length, required.length); index += 1) {
    const a = current[index] ?? 0;
    const b = required[index] ?? 0;
    if (a !== b) return a < b;
  }
  return false;
}
